// apps/web/src/components/visitors/VisitorCard/modes/NormalMode.tsx
import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Divider,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { CalendarToday as CalendarIcon, FilterList as HistoryIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Visitor, ActiveSubscriptionInfo } from '@gym-management/shared';
import VisitorAvatar from '../VisitorAvatar';
import VisitorInfo from '../VisitorInfo';
import VisitorStatusChips from '../VisitorStatusChips';
import VisitorActions from '../VisitorActions';

interface NormalModeProps {
  visitor: Visitor;
  isCheckedIn: boolean;
  hasActiveSubscription?: boolean;
  activeSubscription?: ActiveSubscriptionInfo;
  onEdit?: (visitor: Visitor) => void;
  onDelete?: (id: string) => void;
  onViewDetails?: (visitor: Visitor) => void;
  onShowQR?: (visitor: Visitor) => void;
  onShowHistory?: (visitor: Visitor) => void;
  onCheckIn?: () => void;
  onCheckOut?: () => void;
}

const NormalMode: React.FC<NormalModeProps> = ({
  visitor,
  isCheckedIn,
  hasActiveSubscription,
  activeSubscription,
  onEdit,
  onDelete,
  onViewDetails,
  onShowQR,
  onShowHistory,
  onCheckIn,
  onCheckOut,
}) => {
  const theme = useTheme();
  const formattedDate = format(new Date(visitor.createdAt), 'dd MMM yyyy', { locale: ru });

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: theme.shadows[3],
        },
      }}
    >
      <CardContent
        sx={{
          flexGrow: 1,
          p: 2.5,
          pb: 2,
          display: 'grid',
          gridTemplateRows: 'auto auto 1fr auto',
          gap: 1.5,
        }}
      >
        <Box sx={{ minHeight: '84px' }}>
          <VisitorStatusChips
            hasActiveSubscription={hasActiveSubscription}
            isActive={visitor.active}
            isCheckedIn={isCheckedIn}
            variant="normal"
            subscriptionName={activeSubscription?.subscriptionTypeName}
            remaining={activeSubscription?.subscriptionTypeRemaining}
            subscriptionType={activeSubscription?.subscriptionTypeType}
            totalCount={activeSubscription?.subscriptionTypeCount}
            endDate={activeSubscription?.endDate}
          />
        </Box>

        <Box display="flex" gap={2} alignItems="center">
          <VisitorAvatar
            photoUrl={visitor.photoUrl}
            name={visitor.name}
            isActive={visitor.active}
            size="medium"
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <VisitorInfo name={visitor.name} phone={visitor.phone} variant="normal" />
          </Box>
        </Box>

        <Divider />

        <Box display="flex" alignItems="center" justifyContent="space-between">
          {/* Блок регистрации как в полном режиме: иконка слева, вертикальный текст справа */}
          <Box display="flex" alignItems="center" gap={1}>
            <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Box display="flex" flexDirection="column">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}
              >
                Регистрация
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}
              >
                {formattedDate}
              </Typography>
            </Box>
          </Box>

          {onShowHistory && (
            <Tooltip title="История посещений">
              <IconButton
                size="small"
                onClick={() => onShowHistory(visitor)}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ p: 2.5, pt: 0, gap: 1 }}>
        <VisitorActions
          visitor={visitor}
          onEdit={onEdit}
          onDelete={onDelete}
          onViewDetails={onViewDetails}
          onShowQR={onShowQR}
          onShowHistory={onShowHistory}
          onCheckIn={onCheckIn}
          onCheckOut={onCheckOut}
          isCheckedIn={isCheckedIn}
          variant="buttons"
          size="small"
        />
      </CardActions>
    </Card>
  );
};

export default NormalMode;
